import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Mail, Inbox, Package, Users, ArrowRight, CheckCircle, Clock, 
  Send, Truck, DollarSign, MapPin, RefreshCw, Plus, Eye, 
  Sparkles, ChevronRight, AlertCircle, Phone, CheckSquare
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoadPipeline = () => {
  // State
  const [activeTab, setActiveTab] = useState('inbox');
  const [inboxMessages, setInboxMessages] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Add message modal
  const [addMessageOpen, setAddMessageOpen] = useState(false);
  const [newMessage, setNewMessage] = useState({
    sender_email: '',
    sender_name: '',
    sender_type: 'broker',
    subject: '',
    body: ''
  });
  
  // View opportunity modal
  const [viewOppOpen, setViewOppOpen] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [matchedDrivers, setMatchedDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [driverRate, setDriverRate] = useState('');
  const [sendingOffers, setSendingOffers] = useState(false);
  const [dispatchFeePercent, setDispatchFeePercent] = useState(8); // Default 8% fee
  const [searchRadius, setSearchRadius] = useState(0); // 0 = state only, 50, 100, 200, 500 miles

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [inboxRes, oppsRes, statsRes] = await Promise.all([
        axios.get(`${API}/inbox?limit=50`),
        axios.get(`${API}/opportunities?limit=50`),
        axios.get(`${API}/pipeline/stats`)
      ]);
      setInboxMessages(inboxRes.data);
      setOpportunities(oppsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching pipeline data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Add new message to inbox
  const handleAddMessage = async () => {
    if (!newMessage.sender_email || !newMessage.body) {
      toast.error('Please enter sender email and message body');
      return;
    }
    
    try {
      await axios.post(`${API}/inbox/add`, newMessage);
      toast.success('Message added to inbox');
      setAddMessageOpen(false);
      setNewMessage({ sender_email: '', sender_name: '', sender_type: 'broker', subject: '', body: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add message');
    }
  };

  // Parse message with AI
  const handleParseMessage = async (messageId) => {
    try {
      const res = await axios.post(`${API}/inbox/${messageId}/parse`);
      toast.success('Message parsed successfully');
      
      // Auto-create opportunity
      if (res.data.parsed) {
        await axios.post(`${API}/opportunities/create`, res.data.parsed);
        toast.success('Opportunity created');
      }
      
      fetchData();
    } catch (error) {
      toast.error('Failed to parse message');
    }
  };

  // View opportunity and find drivers
  const handleViewOpportunity = async (opp) => {
    setSelectedOpp(opp);
    setViewOppOpen(true);
    setMatchedDrivers([]);
    setSelectedDrivers([]);
    
    try {
      const res = await axios.post(`${API}/opportunities/${opp.id}/find-drivers?limit=100&radius_miles=${searchRadius}`);
      setMatchedDrivers(res.data.drivers || []);
    } catch (error) {
      console.error('Error finding drivers:', error);
    }
  };

  // Re-search drivers with new radius
  const handleSearchWithRadius = async (radius) => {
    setSearchRadius(radius);
    setSelectedDrivers([]);
    if (!selectedOpp) return;
    
    try {
      const res = await axios.post(`${API}/opportunities/${selectedOpp.id}/find-drivers?limit=100&radius_miles=${radius}`);
      setMatchedDrivers(res.data.drivers || []);
      toast.success(`Found ${res.data.count} drivers within ${radius === 0 ? 'state' : radius + ' miles'}`);
    } catch (error) {
      toast.error('Failed to search drivers');
    }
  };

  // Send offers to drivers
  const handleSendOffers = async () => {
    if (selectedDrivers.length === 0) {
      toast.error('Please select at least one driver');
      return;
    }
    if (!driverRate) {
      toast.error('Please enter driver rate');
      return;
    }
    
    setSendingOffers(true);
    try {
      const res = await axios.post(`${API}/opportunities/${selectedOpp.id}/send-offers`, {
        opportunity_id: selectedOpp.id,
        driver_ids: selectedDrivers,
        driver_rate: parseFloat(driverRate)
      });
      
      toast.success(`Sent offers to ${res.data.sent} drivers`);
      setViewOppOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to send offers');
    } finally {
      setSendingOffers(false);
    }
  };

  // Toggle driver selection
  const toggleDriverSelection = (driverId) => {
    if (selectedDrivers.includes(driverId)) {
      setSelectedDrivers(selectedDrivers.filter(id => id !== driverId));
    } else {
      setSelectedDrivers([...selectedDrivers, driverId]);
    }
  };

  // Select All / Deselect All drivers
  const handleSelectAllDrivers = () => {
    if (selectedDrivers.length === matchedDrivers.length) {
      // Deselect all
      setSelectedDrivers([]);
    } else {
      // Select all
      const allDriverIds = matchedDrivers.map(d => d.id || d.email);
      setSelectedDrivers(allDriverIds);
    }
  };

  // Calculate suggested driver rate based on broker rate and fee percentage
  const calculateSuggestedRate = (brokerRate) => {
    if (!brokerRate) return '';
    return Math.round(brokerRate * (1 - dispatchFeePercent / 100)).toString();
  };

  return (
    <Layout>
      <Toaster position="top-center" richColors />
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Package className="w-6 h-6 text-blue-400" />
              Load Pipeline
            </h1>
            <p className="text-slate-400">Broker Email → Load Opportunity → Driver Offer</p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Pipeline Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-blue-500/10 border-blue-500/30">
              <CardContent className="p-4 text-center">
                <Inbox className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stats.inbox?.new || 0}</p>
                <p className="text-xs text-slate-400">New Emails</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-500/10 border-purple-500/30">
              <CardContent className="p-4 text-center">
                <Sparkles className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stats.inbox?.parsed || 0}</p>
                <p className="text-xs text-slate-400">Parsed</p>
              </CardContent>
            </Card>
            <Card className="bg-orange-500/10 border-orange-500/30">
              <CardContent className="p-4 text-center">
                <Package className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stats.opportunities?.open || 0}</p>
                <p className="text-xs text-slate-400">Open Opportunities</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-500/10 border-yellow-500/30">
              <CardContent className="p-4 text-center">
                <Send className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stats.opportunities?.drivers_contacted || 0}</p>
                <p className="text-xs text-slate-400">Drivers Contacted</p>
              </CardContent>
            </Card>
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="p-4 text-center">
                <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stats.opportunities?.assigned || 0}</p>
                <p className="text-xs text-slate-400">Assigned</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800">
            <TabsTrigger value="inbox" className="gap-2">
              <Inbox className="w-4 h-4" />
              Inbox ({inboxMessages.length})
            </TabsTrigger>
            <TabsTrigger value="opportunities" className="gap-2">
              <Package className="w-4 h-4" />
              Opportunities ({opportunities.length})
            </TabsTrigger>
          </TabsList>

          {/* INBOX TAB */}
          <TabsContent value="inbox" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setAddMessageOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Email / Message
              </Button>
            </div>

            {inboxMessages.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-12 text-center">
                  <Inbox className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">No Messages Yet</h3>
                  <p className="text-slate-400 mb-4">Paste broker/shipper emails here to start the pipeline</p>
                  <Button onClick={() => setAddMessageOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Email
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {inboxMessages.map((msg) => (
                  <Card key={msg.id} className={`bg-slate-800/50 border-slate-700 ${msg.status === 'new' ? 'border-l-4 border-l-blue-500' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              msg.sender_type === 'broker' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                            }`}>
                              {msg.sender_type}
                            </span>
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              msg.status === 'new' ? 'bg-yellow-500/20 text-yellow-400' :
                              msg.status === 'parsed' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>
                              {msg.status}
                            </span>
                          </div>
                          <p className="text-white font-semibold">{msg.subject || '(No Subject)'}</p>
                          <p className="text-sm text-slate-400">From: {msg.sender_email}</p>
                          <p className="text-sm text-slate-500 mt-2 line-clamp-2">{msg.body}</p>
                        </div>
                        <div className="flex gap-2">
                          {msg.status === 'new' && (
                            <Button 
                              size="sm" 
                              onClick={() => handleParseMessage(msg.id)}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              <Sparkles className="w-4 h-4 mr-1" />
                              Parse & Create
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* OPPORTUNITIES TAB */}
          <TabsContent value="opportunities" className="space-y-4">
            {opportunities.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">No Opportunities Yet</h3>
                  <p className="text-slate-400">Parse emails from the Inbox to create opportunities</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {opportunities.map((opp) => (
                  <Card key={opp.id} className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 text-xs rounded font-semibold ${
                              opp.status === 'open' ? 'bg-orange-500/20 text-orange-400' :
                              opp.status === 'drivers_contacted' ? 'bg-yellow-500/20 text-yellow-400' :
                              opp.status === 'assigned' ? 'bg-green-500/20 text-green-400' :
                              'bg-slate-500/20 text-slate-400'
                            }`}>
                              {opp.status?.replace('_', ' ').toUpperCase()}
                            </span>
                            <span className="px-2 py-0.5 text-xs rounded bg-blue-500/20 text-blue-400">
                              {opp.equipment_type?.toUpperCase()}
                            </span>
                            {opp.frequency && opp.frequency !== 'one-time' && (
                              <span className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-400">
                                {opp.frequency.toUpperCase()}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 text-white">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4 text-green-400" />
                              <span className="font-semibold">{opp.pickup_city}, {opp.pickup_state}</span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-slate-500" />
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4 text-red-400" />
                              <span>{opp.delivery_cities?.join(', ') || opp.delivery_states?.join(', ')}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                            <span>From: {opp.broker_email}</span>
                            {opp.rate && <span className="text-green-400 font-semibold">${opp.rate.toLocaleString()}</span>}
                            {opp.weight_lbs && <span>{opp.weight_lbs.toLocaleString()} lbs</span>}
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          {opp.status === 'open' && (
                            <Button 
                              onClick={() => handleViewOpportunity(opp)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Users className="w-4 h-4 mr-2" />
                              Find Drivers
                            </Button>
                          )}
                          {opp.status === 'drivers_contacted' && (
                            <Button 
                              onClick={() => handleViewOpportunity(opp)}
                              variant="outline"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* ADD MESSAGE DIALOG */}
        <Dialog open={addMessageOpen} onOpenChange={setAddMessageOpen}>
          <DialogContent className="max-w-2xl glass-card">
            <DialogHeader>
              <DialogTitle>Add Broker/Shipper Email</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sender Email *</Label>
                  <Input
                    value={newMessage.sender_email}
                    onChange={(e) => setNewMessage({...newMessage, sender_email: e.target.value})}
                    placeholder="broker@company.com"
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
                <div>
                  <Label>Sender Name</Label>
                  <Input
                    value={newMessage.sender_name}
                    onChange={(e) => setNewMessage({...newMessage, sender_name: e.target.value})}
                    placeholder="John Smith"
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
              </div>
              <div>
                <Label>Type</Label>
                <select
                  value={newMessage.sender_type}
                  onChange={(e) => setNewMessage({...newMessage, sender_type: e.target.value})}
                  className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-600 text-white"
                >
                  <option value="broker">Broker</option>
                  <option value="shipper">Shipper</option>
                  <option value="driver">Driver</option>
                </select>
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})}
                  placeholder="RE: Flatbed loads available"
                  className="bg-slate-900 border-slate-600"
                />
              </div>
              <div>
                <Label>Email Body *</Label>
                <textarea
                  value={newMessage.body}
                  onChange={(e) => setNewMessage({...newMessage, body: e.target.value})}
                  placeholder="Paste the full email content here..."
                  className="w-full h-48 p-3 rounded-md bg-slate-900 border border-slate-600 text-white"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAddMessageOpen(false)}>Cancel</Button>
                <Button onClick={handleAddMessage} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Inbox
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* VIEW OPPORTUNITY & SEND OFFERS DIALOG */}
        <Dialog open={viewOppOpen} onOpenChange={setViewOppOpen}>
          <DialogContent className="max-w-4xl glass-card max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedOpp?.pickup_city}, {selectedOpp?.pickup_state} → {selectedOpp?.delivery_cities?.join(', ')}
              </DialogTitle>
            </DialogHeader>
            
            {selectedOpp && (
              <div className="space-y-4 mt-4">
                {/* Opportunity Details */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-slate-400 text-xs">Equipment</p>
                        <p className="text-white font-semibold">{selectedOpp.equipment_type?.toUpperCase()}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs">Weight</p>
                        <p className="text-white font-semibold">{selectedOpp.weight_lbs?.toLocaleString() || 'TBD'} lbs</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs">Broker Rate</p>
                        <p className="text-green-400 font-semibold">${selectedOpp.rate?.toLocaleString() || 'TBD'}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs">Frequency</p>
                        <p className="text-purple-400 font-semibold">{selectedOpp.frequency?.toUpperCase()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Driver Rate Input - ENHANCED with Smart Rate */}
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-green-400 font-semibold flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Pricing Calculator
                    </Label>
                    {selectedOpp.rate && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDriverRate(Math.round(selectedOpp.rate * (1 - dispatchFeePercent / 100)).toString())}
                        className="text-xs"
                      >
                        💡 Suggest Rate ({100 - dispatchFeePercent}% of ${selectedOpp.rate?.toLocaleString()})
                      </Button>
                    )}
                  </div>
                  
                  {/* Fee Percentage Selector */}
                  <div className="flex items-center gap-4 mb-4 p-2 bg-slate-800 rounded-lg">
                    <span className="text-slate-400 text-sm">Your Dispatch Fee:</span>
                    <div className="flex items-center gap-2">
                      {[5, 6, 7, 8, 10, 12].map((pct) => (
                        <button
                          key={pct}
                          onClick={() => {
                            setDispatchFeePercent(pct);
                            if (selectedOpp?.rate) {
                              setDriverRate(Math.round(selectedOpp.rate * (1 - pct / 100)).toString());
                            }
                          }}
                          className={`px-3 py-1 rounded text-sm font-bold transition-colors ${
                            dispatchFeePercent === pct 
                              ? 'bg-green-600 text-white' 
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          {pct}%
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-slate-400 text-xs mb-1">Broker Pays You</p>
                      <p className="text-2xl font-bold text-white">${selectedOpp.rate?.toLocaleString() || 'TBD'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs mb-1">You Pay Driver</p>
                      <Input
                        type="number"
                        value={driverRate}
                        onChange={(e) => setDriverRate(e.target.value)}
                        placeholder="Enter rate"
                        className="bg-slate-900 border-slate-600 text-lg font-bold"
                      />
                    </div>
                    <div className="text-right">
                      <p className="text-slate-400 text-xs mb-1">Your Profit 💰</p>
                      <p className={`text-2xl font-bold ${
                        driverRate && selectedOpp.rate && (selectedOpp.rate - parseFloat(driverRate)) > 0 
                          ? 'text-green-400' 
                          : 'text-red-400'
                      }`}>
                        ${driverRate && selectedOpp.rate 
                          ? (selectedOpp.rate - parseFloat(driverRate)).toLocaleString() 
                          : '0'}
                      </p>
                      {driverRate && selectedOpp.rate && (
                        <p className="text-xs text-slate-500">
                          {((selectedOpp.rate - parseFloat(driverRate)) / selectedOpp.rate * 100).toFixed(1)}% margin
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Matched Drivers */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-semibold">
                      Matched Drivers ({matchedDrivers.length})
                    </h3>
                    <div className="flex items-center gap-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleSelectAllDrivers}
                        className="text-xs"
                      >
                        {selectedDrivers.length === matchedDrivers.length ? '✓ Deselect All' : '☑ Select All'}
                      </Button>
                      <span className="text-sm text-green-400">{selectedDrivers.length} selected</span>
                    </div>
                  </div>
                  
                  {/* Radius Selector */}
                  <div className="flex items-center gap-2 mb-3 p-2 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <span className="text-blue-400 text-sm font-semibold">📍 Search Radius:</span>
                    <div className="flex flex-wrap gap-1">
                      {[
                        { value: 0, label: 'State Only' },
                        { value: 100, label: '100 mi' },
                        { value: 200, label: '200 mi' },
                        { value: 500, label: '500 mi' }
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => handleSearchWithRadius(opt.value)}
                          className={`px-3 py-1 rounded text-xs font-bold transition-colors ${
                            searchRadius === opt.value 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {matchedDrivers.map((driver) => (
                      <div 
                        key={driver.id || driver.email}
                        className={`p-3 rounded-lg transition-colors ${
                          selectedDrivers.includes(driver.id || driver.email)
                            ? 'bg-green-500/20 border border-green-500'
                            : 'bg-slate-800 border border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => toggleDriverSelection(driver.id || driver.email)}
                          >
                            <p className="text-white font-medium">{driver.name}</p>
                            <p className="text-xs text-slate-500">{driver.city || driver.home_base}, {driver.state}</p>
                          </div>
                          
                          {/* Quick Contact Buttons */}
                          <div className="flex items-center gap-1">
                            {/* Quo Button (Mobile) */}
                            <a 
                              href={`openphone://dial?number=${encodeURIComponent(driver.phone)}&action=call`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 px-2 py-1 bg-purple-600 hover:bg-purple-700 rounded text-white text-xs font-bold"
                              title="Call via Quo"
                            >
                              📱 Quo
                            </a>
                            {/* Regular Phone */}
                            <a 
                              href={`tel:${driver.phone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-700 rounded text-white text-xs font-bold"
                              title={`Call ${driver.phone}`}
                            >
                              <Phone className="w-3 h-3" />
                              {driver.phone}
                            </a>
                            {/* Email */}
                            <a 
                              href={`mailto:${driver.email}?subject=Load Opportunity`}
                              onClick={(e) => e.stopPropagation()}
                              className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded text-white"
                              title={`Email ${driver.email}`}
                            >
                              <Mail className="w-3 h-3" />
                            </a>
                            {/* Select */}
                            <button
                              onClick={() => toggleDriverSelection(driver.id || driver.email)}
                              className={`p-1.5 rounded ${
                                selectedDrivers.includes(driver.id || driver.email)
                                  ? 'bg-green-500 text-white'
                                  : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                              }`}
                            >
                              <CheckSquare className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Send Offers Button */}
                <div className="flex justify-end gap-2 pt-4 border-t border-slate-700">
                  <Button variant="outline" onClick={() => setViewOppOpen(false)}>Cancel</Button>
                  <Button 
                    onClick={handleSendOffers}
                    disabled={selectedDrivers.length === 0 || !driverRate || sendingOffers}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {sendingOffers ? 'Sending...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Offers to {selectedDrivers.length} Drivers
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default LoadPipeline;
